import React, { useEffect, useState, useRef } from "react";
import { SlTab, SlTabGroup, SlTabPanel, SlDrawer } from "@shoelace-style/shoelace/dist/react";
import { useTableState } from "../Designer/utils/useTableState";
import AssignedAds from "./AssignedAds";
import ProgressAds from "./ProgressAds";
import Completed from "./CompleteAds";
import api from "../../services/api";
import config from "../../config";
import { Toast } from "../../shared/Toast";
import RectangleClassifieds from "../../creativePreviews/RectangleClassifieds";
import PromotedImagePreview from "../../creativePreviews/PromotedImagePreview";
import { proxyDownload } from "../../utils/proxyDownload";
import Toggle from "../../utils/Toggle";
import Magnifier from "../../utils/Magnifier";

const FullPreview = ({ images, currentIndex, isTextEditing, setIsTextEditing, setCurrentIndex, editingText, handleEditText, setEditingText, handleSaveText }) => {
  const current = images[currentIndex];

  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [isZoomed, setIsZoomed] = useState(false);
  const imgRef = useRef(null);

  const handleMouseMove = (e) => {
    const elem = imgRef.current;
    if (!elem) return;

    const { top, left, width, height } = elem.getBoundingClientRect();
    const x = ((e.pageX - left - window.pageXOffset) / width) * 100;
    const y = ((e.pageY - top - window.pageYOffset) / height) * 100;

    setPosition({ x, y });
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full h-[80vh] gap-4">
        <Toggle open = {showMagnifier} setOpen={setShowMagnifier} title={"Zoom"}/>
        <div className="relative flex items-center justify-center w-full h-[80%] bg-white rounded-lg shadow-md overflow-hidden">
          <button onClick={handlePrev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2" aria-label="Previous Image">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="relative bg-white rounded-lg shadow-lg p-2" onMouseMove={handleMouseMove}>
            <img ref={imgRef} src={current?.url} alt={`creative-${currentIndex}`} className="max-w-[90%] max-h-[90%] object-cover cursor-zoom-in" onClick={() => setIsZoomed(true)} />
          </div>
          {showMagnifier ? <Magnifier showMagnifier={showMagnifier} zoomLevel={5} url={current?.url} position={position} /> : null}
          <button onClick={handleNext} className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2" aria-label="Next Image">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className={`absolute top-2 right-2 w-12 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${current.type === "main" ? "bg-green-600" : "bg-orange-600"}`}>
            {current.type === "main" ? "MAIN" : "SUB"}
          </div>
        </div>

        {current.type === "sub" && (
          <div className="text-sm text-black px-2 py-1 bg-yellow-200 rounded">
            {isTextEditing ? (
              <div className="flex items-center gap-2">
                <input type="text" className="px-2 py-1 rounded border" value={editingText.text} onChange={(e) => setEditingText((prev) => ({ ...prev, text: e.target.value }))} />
                <button className="px-2 py-1 rounded bg-green-500 text-white" onClick={handleSaveText}>
                  Save
                </button>
                <button
                  className="px-2 py-1 rounded bg-gray-300"
                  onClick={() => {
                    setIsTextEditing(false);
                    setEditingText({ url: null, text: "" });
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span>{"Text: " + current.text}</span>
                <button
                  className="px-2 py-1 rounded bg-gray-200"
                  onClick={() => {
                    setIsTextEditing(true);
                    handleEditText(current.url, current.text);
                  }}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500">
          Image <span className="font-medium">{currentIndex + 1}</span> of {images.length}
        </p>
      </div>

      {isZoomed && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center" onClick={() => setIsZoomed(false)}>
          <img src={current.url} alt={`zoomed-${currentIndex}`} className="max-w-full max-h-full object-contain cursor-zoom-out" />
        </div>
      )}
    </>
  );
};

const ThumbnailPreview = ({ images, currentIndex, setCurrentIndex }) => (
  <div className="flex flex-wrap gap-2 my-2 py-1 bg-gray-50 border-t">
    {images.map((imageObj, idx) => (
      <div key={idx} className="relative">
        <img
          src={imageObj.url}
          alt={`thumb-${idx}`}
          onClick={() => setCurrentIndex(idx)}
          className={`w-12 h-12 object-cover rounded cursor-pointer border transition-transform ${idx === currentIndex ? "border-blue-500 scale-105" : "border-gray-300 hover:border-blue-300"}`}
        />
        <div className={`absolute top-0 right-0 px-1 text-xs font-bold text-white rounded-bl ${imageObj.type === "main" ? "bg-green-600" : "bg-orange-600"}`}>
          {imageObj.type === "main" ? "M" : "S"}
        </div>
      </div>
    ))}
  </div>
);

const WriteContentForm = ({ selectedForm, variations, handleVariationChange, setSelectedForm, adType }) => {
  const max_headline_limit = adType === "promoted_image_story" ? 75 : 32;
  const max_description_limit = adType === "promoted_image_story" ? 400 : 90;
  const show_headline = adType === "promoted_image_story" || adType === "classifiedads";

  return (
    <div className="flex flex-col gap-4 font-lg max-w-[70vh]">
      <div className="flex gap-2">
        {[1, 2, 3].map((num) => (
          <button key={num} className={`px-3 py-1 rounded  ${selectedForm === num ? "bg-blue-600 text-white" : "bg-gray-200"}`} onClick={() => setSelectedForm(num)}>
            Variation {num}
          </button>
        ))}
      </div>

      {show_headline && (
        <div>
          <label className="block text-xs  text-gray-700">Headline (Max {max_headline_limit} characters)</label>
          <input
            type="text"
            value={variations[selectedForm - 1].headline}
            onChange={(e) => handleVariationChange(selectedForm, "headline", e.target.value)}
            placeholder="Headline"
            className="w-full border px-2 py-1 rounded text-sm"
            maxLength={max_headline_limit}
          />
          <div className="text-right  text-green-800">{max_headline_limit - variations[selectedForm - 1]?.headline?.trim().length} characters</div>
        </div>
      )}
      <div>
        <label className="block  font-medium text-gray-700">Description {show_headline && `(Max ${max_description_limit} characters)`}</label>
        <textarea
          value={variations[selectedForm - 1].description}
          onChange={(e) => handleVariationChange(selectedForm, "description", e.target.value)}
          placeholder="Description"
          rows="25"
          className="w-full border max-h-[50vh] px-2 py-1 rounded"
          {...(show_headline ? { maxLength: max_description_limit } : null)}
        />
        {show_headline && <div className="text-right  text-green-800">{max_description_limit - variations[selectedForm - 1]?.description?.trim().length} characters</div>}
      </div>
    </div>
  );
};

const ContentWriterHome = () => {
  const [designs, setDesigns] = useState([]);
  const [loader, setLoader] = useState(false);
  const [totalAssigned, setTotalAssigned] = useState(0);
  const [totalProgress, setTotalProgress] = useState(0);
  const [totalComplete, setTotalComplete] = useState(0);
  const [currentChild, setCurrentChild] = useState("assigned-Ads");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [variations, setVariations] = useState([
    { id: 1, headline: "", description: "" },
    { id: 2, headline: "", description: "" },
    { id: 3, headline: "", description: "" },
  ]);
  const [selectedForm, setSelectedForm] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [adData, setAdData] = useState(null);
  const [isTextEditing, setIsTextEditing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editingText, setEditingText] = useState({ url: null, text: "" });
  const [previewMode, setPreviewMode] = useState("full");
  const tabGroupRef = useRef(null);
  const { currentPage, setCurrentPage, handlePageChange } = useTableState();
  const [isDownloading,setIsDownloading] = useState(false);

  useEffect(() => {
    if (adData) setCurrentIndex(0);
  }, [adData]);

  const fetchAdData = async (creative_id) => {
    try {
      const res = await api({ url: `/user/content_writer/get-ad-details`, method: "POST", data: { creative_id } });
      if (res.code === 200) setAdData(res.data[0]);
    } catch (error) {
      Toast("error", "Failed to load ad details");
    }
  };

  const handleWriteContentClick = (row, formNumber) => {
    setSelectedRow(row);
    setSelectedForm(formNumber);
    fetchAdData(row.creative_id);
    setIsDrawerOpen(true);
  };

  const handleVariationChange = (id, field, value) => {
    if (field === "form") setSelectedForm(value);
    else setVariations((prev) => prev.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
  };

  const handleEditText = (url, currentText) => {
    setEditingText({ url, text: currentText });
  };

  const handleSaveText = async () => {
    if (!editingText.url || !selectedRow) return;

    try {
      const res = await api({
        url: `/user/content_writer/editText`,
        method: "POST",
        data: { creative_id: selectedRow.creative_id, url: editingText.url, text: editingText.text },
      });

      if (res.code === 200) {
        Toast("success", "Text updated successfully");
        setEditingText({ url: null, text: "" });
        setIsTextEditing(false);
        fetchAdData(selectedRow.creative_id);
      }
    } catch (error) {
      Toast("error", "Failed to update text");
    }
  };

  const hasValidContent = () => {
    if (selectedRow?.ad_type === "promoted_image_story" || selectedRow?.ad_type === "classifiedads") {
      return variations.some((v) => v.headline.trim() && v.description.trim());
    }
    return variations.some((v) => v.description.trim());
  };

  const handleUpload = async () => {
    if (!selectedRow || !hasValidContent()) {
      Toast("error", selectedRow?.ad_type === "promoted_image_story" || selectedRow?.ad_type === "classifiedads" ? "All fields required" : "Description required");
      return;
    }
    try {
      const res = await api({
        url: `/user/content_writer/uploadContent`,
        method: "POST",
        data: { content_id: selectedRow.content_id, creative_id: selectedRow.creative_id, variations },
      });
      if (res.status === 'SUCCESS') {
        setIsDrawerOpen(false);
        if (currentChild === "progress-Ads") handleTabSwitch("complete-Ads");
        else getCompletedDesigns(currentPage);
        Toast("success", "Content uploaded successfully");
      }else{
        console.log(res)
        Toast('error',res?.data?.message || "Failed to upload content")
      }
    } catch (err) {
      Toast("error", err?.message);
    }
  };

  const totalCount = async () => {
    try {
      const res = await api({ url: `/user/content_writer/getTotalCount`, method: "POST", data: {} });
      if (res.code === 200) {
        setTotalAssigned(res.data.filter((item) => item.status === 0).reduce((sum, item) => sum + parseInt(item.total), 0));
        setTotalProgress(res.data.filter((item) => item.status === 1).reduce((sum, item) => sum + parseInt(item.total), 0));
        setTotalComplete(res.data.filter((item) => item.status === 2).reduce((sum, item) => sum + parseInt(item.total), 0));
      }
    } catch (error) {}
  };

  useEffect(() => {
    totalCount();
  }, [currentChild]);

  const closeModal = () => {
    setIsDrawerOpen(false);
    setAdData(null);
    setVariations([
      { id: 1, headline: "", description: "" },
      { id: 2, headline: "", description: "" },
      { id: 3, headline: "", description: "" },
    ]);
    setEditingText({ url: null, text: "", isOpen: false });
  };

  useEffect(() => {
    if (isDrawerOpen && selectedRow?.content?.length) setVariations(selectedRow.content);
  }, [isDrawerOpen, selectedRow]);

  const handleTabSwitch = (panelName) => {
    tabGroupRef.current?.show(panelName);
  };

  const getAssignedDesigns = async (page) => {
    setLoader(true);
    try {
      const res = await api({ url: `/user/content_writer/getAssignedDesigns`, method: "POST", data: { page, limit: config.PER_PAGE } });
      if (res.code === 200) setDesigns(res.data?.data);
    } catch (error) {}
    setLoader(false);
  };

  const getProgressDesigns = async (page) => {
    setLoader(true);
    try {
      const res = await api({ url: `/user/content_writer/getProgressedDesigns`, method: "POST", data: { page, limit: config.PER_PAGE } });
      if (res.code === 200) setDesigns(res.data?.data);
    } catch (error) {}
    setLoader(false);
  };

  const getCompletedDesigns = async (page) => {
    setLoader(true);
    try {
      const res = await api({ url: `/user/content_writer/getCompletedDesigns`, method: "POST", data: { page, limit: config.PER_PAGE } });
      if (res.code === 200) setDesigns(res.data?.data);
    } catch (error) {}
    setLoader(false);
  };

  const pickDesign = async (content_id, creative_id) => {
    try {
      const res = await api({ url: `/user/content_writer/pickDesign`, method: "POST", data: { content_id, creative_id } });
      if (res.code === 200) {
        Toast("success", "Design picked successfully");
        handleTabSwitch("progress-Ads");
      }
    } catch (error) {
      Toast("error", "Failed to pick design");
    }
  };
  const handleDownloadImages = async(images,filename) =>{
    try{
      if(isDownloading) return;
      setIsDownloading(true);
      const imgUrls = images.map(image => image.url)
      await proxyDownload(imgUrls,filename);
    }catch(error){
       console.error(error);
    }finally{
       setIsDownloading(false);
    }
  }
  useEffect(() => {
    if (currentChild === "assigned-Ads") getAssignedDesigns(currentPage);
    else if (currentChild === "progress-Ads") getProgressDesigns(currentPage);
    else getCompletedDesigns(currentPage);
  }, [currentChild, currentPage]);

  return (
    <div className="p-4">
      <SlTabGroup ref={tabGroupRef} onSlTabShow={(event) => setCurrentChild(event.detail.name)}>
        <SlTab slot="nav" panel="assigned-Ads">
          Assigned <span className="text-blue-600 mx-1">{totalAssigned || 0}</span>
        </SlTab>
        <SlTab slot="nav" panel="progress-Ads">
          In Progress <span className="text-blue-600 mx-1">{totalProgress || 0}</span>
        </SlTab>
        <SlTab slot="nav" panel="complete-Ads">
          Completed <span className="text-blue-600 mx-1">{totalComplete || 0}</span>
        </SlTab>
        <SlTabPanel name="assigned-Ads">
          <AssignedAds
            Designs={designs}
            setDesigns={setDesigns}
            loader={loader}
            setLoader={setLoader}
            pickDesign={pickDesign}
            totalAssigned={totalAssigned}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            handlePageChange={handlePageChange}
          />
        </SlTabPanel>
        <SlTabPanel name="progress-Ads">
          <ProgressAds
            Designs={designs}
            setDesigns={setDesigns}
            loader={loader}
            setLoader={setLoader}
            handleWriteContentClick={handleWriteContentClick}
            totalProgress={totalProgress}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            handlePageChange={handlePageChange}
          />
        </SlTabPanel>
        <SlTabPanel name="complete-Ads">
          <Completed
            Designs={designs}
            setDesigns={setDesigns}
            loader={loader}
            setLoader={setLoader}
            handleWriteContentClick={handleWriteContentClick}
            totalComplete={totalComplete}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            handlePageChange={handlePageChange}
          />
        </SlTabPanel>
      </SlTabGroup>
      <SlDrawer label="Ad Preview & Content" style={{ "--size": "100vw" }} placement="end" open={isDrawerOpen} onSlAfterHide={closeModal}>
        <div className="flex h-full p-4 gap-4">
          {adData ? (
            (() => {
              const imgUrls = adData.img_urls || {};
              const mainImage =  imgUrls.main;
              const subImages = imgUrls.sub;
              const classifiedImageUrl = imgUrls.classified;
              const thumbnailUrl = imgUrls.thumbnail;
              let images = [];
              if(mainImage) images.push({type:"main", url: mainImage, text: null});
              if(subImages) images=[...images,...subImages.map(obj => typeof obj === 'string' ? ({ url: obj, text: null, type: 'sub' }) : ({ url: obj.url, text: obj.text, type: 'sub' }))];
              if(classifiedImageUrl) images.push({type:'classified',url:classifiedImageUrl,text:null});
              if(thumbnailUrl) images.push({type:'thumbnail',url:thumbnailUrl,text:null});
              
              return (
                <>
                  <div className="w-2/3 flex flex-col gap-2">
                    {adData.creative_name && <h6>Campaign Name : {adData.creative_name}</h6>}
                    <button className={`mx-3 px-2 py-1 rounded-lg shadow-md ${isDownloading ? 'bg-green-500 cursor-progress':'bg-blue-400'}`} onClick={()=>handleDownloadImages(images,adData.creative_name)} disabled={isDownloading}>Download Images</button>
                    {/* Preview Toggle */}
                    {(adData.ad_creative_sub_type === "classifiedads" || adData.ad_creative_sub_type === "promoted_image_story") && (
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold">Preview</h4>
                        <div className="flex bg-gray-200 rounded-lg p-1">
                          <button
                            className={`px-3 py-1 text-xs rounded-md transition-all ${previewMode === "live" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
                            onClick={() => setPreviewMode("live")}
                          >
                            Live Preview
                          </button>
                          <button
                            className={`px-3 py-1 text-xs rounded-md transition-all ${previewMode === "full" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
                            onClick={() => setPreviewMode("full")}
                          >
                            Full Images
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Preview Content */}
                    {previewMode === "live" ? (
                      <div className="flex-1 flex items-center justify-center">
                        {adData.ad_type === "classified" && (
                          <RectangleClassifieds
                            preview={{
                              img_urls: adData.img_urls?.classified || adData.cla_brand_img,
                              headline: variations[selectedForm - 1].headline || adData.head_line || adData.headline,
                              description: variations[selectedForm - 1].description || adData.description,
                              logo_urls: adData.cla_brand_img,
                              brand_name: adData.cla_brand_name,
                              caption: adData.cla_brand_caption,
                              button_enabled: adData.cla_btn_txt ? 1 : 0,
                              button_text: adData.cla_btn_txt || "",
                              btn_text_bgclr: adData.cla_btn_bg_clr || "#000000",
                              btn_text_clr: adData.cla_btn_txt_col || "#ffffff",
                              name: adData.creative_name,
                            }}
                          />
                        )}
                        {adData.ad_creative_sub_type === "promoted_image_story" && (
                          <PromotedImagePreview
                            preview={{
                              img_urls: adData.img_urls?.main,
                              head_line: variations[selectedForm - 1].headline || adData.head_line,
                              description: variations[selectedForm - 1].description || adData.description,
                              click_url: adData.click_url,
                              button_enabled: adData.button_enabled || 0,
                              button_text: adData.button_text || "",
                              btn_text_bgclr: adData.btn_text_bgclr || "#000000",
                              btn_text_clr: adData.btn_text_clr || "#ffffff",
                              name: adData.creative_name,
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <div className="flex-1">
                        {adData.ad_type !== "classified" && images.length > 0 && (
                          <ThumbnailPreview images={images} currentIndex={currentIndex} setCurrentIndex={setCurrentIndex} selectedRow={selectedRow} />
                        )}
                        {adData.ad_type === "classified" ? (
                          classifiedImageUrl ? (
                            <img src={classifiedImageUrl} alt="Classified Ad" className="w-full h-[80%] object-contain rounded-lg" />
                          ) : (
                            <div className="flex items-center justify-center h-[80%] bg-gray-100 rounded-lg">
                              <p className="text-gray-500">No Image Available</p>
                            </div>
                          )
                        ) : images.length > 0 ? (
                          <FullPreview
                            images={images}
                            currentIndex={currentIndex}
                            setCurrentIndex={setCurrentIndex}
                            handleEditText={handleEditText}
                            editingText={editingText}
                            setEditingText={setEditingText}
                            handleSaveText={handleSaveText}
                            isTextEditing={isTextEditing}
                            setIsTextEditing={setIsTextEditing}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-[80%] bg-gray-100 rounded-lg">
                            <p className="text-gray-500">No Image Available</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="w-1/3 p-4 bg-gray-50 rounded-lg flex flex-col gap-4">
                    <h4 className="font-semibold">Content Writing</h4>
                    <WriteContentForm
                      selectedForm={selectedForm}
                      variations={variations}
                      setSelectedForm={setSelectedForm}
                      handleVariationChange={handleVariationChange}
                      adType={selectedRow?.ad_creative_sub_type}
                    />
                    <button onClick={handleUpload} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                      Upload Content
                    </button>
                  </div>
                </>
              );
            })()
          ) : (
            <div className="flex items-center justify-center h-full w-full">
              <p className="text-gray-500">Loading...</p>
            </div>
          )}
        </div>
      </SlDrawer>
    </div>
  );
};

export default ContentWriterHome;
